import {format} from 'date-fns';
import {User, Clock, ArrowRight, Building2} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@common-web/components/ui/sheet';
import {Card, CardContent} from '@common-web/components/ui/card';
import {RouterOutput} from '@common-web/lib/trpc';
import {AuditActionBadge, entityTypeLabel} from './auditFormat';

type AuditEntry = RouterOutput['internal']['auditLog']['list']['entries'][number];

export function AuditEntrySheet({entry, onClose}: {entry: AuditEntry; onClose: () => void}) {
  const hasDiff = entry.fieldChanged || entry.oldValue !== null || entry.newValue !== null;
  const detailsJson =
    entry.details != null ? JSON.stringify(entry.details, null, 2) : null;

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className='sm:max-w-lg overflow-y-auto'>
        <SheetHeader className='pb-4 border-b'>
          <SheetTitle className='text-xl font-bold text-brand-navy flex items-center gap-2'>
            <AuditActionBadge action={entry.action} />
          </SheetTitle>
          <SheetDescription className='flex flex-col gap-1 text-sm'>
            <span>
              <span className='font-medium text-brand-gray'>{entityTypeLabel(entry.entityType)}:</span>{' '}
              <span className='text-brand-navy'>{entry.entityName || entry.entityId}</span>
            </span>
            <span className='font-mono text-xs text-brand-gray'>{entry.entityId}</span>
          </SheetDescription>
        </SheetHeader>

        <div className='space-y-5 mt-6'>
          {/* Actor + timestamp */}
          <div className='grid grid-cols-1 gap-3'>
            <div className='flex items-center gap-2 text-sm'>
              <User className='h-4 w-4 text-brand-blue' />
              <span className='text-brand-gray'>Actor:</span>
              <span className='font-medium text-brand-navy'>{entry.changedBy}</span>
            </div>
            <div className='flex items-center gap-2 text-sm'>
              <Clock className='h-4 w-4 text-brand-gray' />
              <span className='text-brand-gray'>When:</span>
              <span className='font-medium text-brand-navy'>
                {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm:ss a')}
              </span>
            </div>
            {entry.clientName && (
              <div className='flex items-center gap-2 text-sm'>
                <Building2 className='h-4 w-4 text-brand-gray' />
                <span className='text-brand-gray'>Tenant:</span>
                <span className='font-medium text-brand-navy'>{entry.clientName}</span>
              </div>
            )}
          </div>

          {/* Field-level diff */}
          {hasDiff && (
            <Card className='border-gray-200'>
              <CardContent className='pt-4 space-y-3'>
                {entry.fieldChanged && (
                  <div className='text-sm'>
                    <span className='text-brand-gray'>Field: </span>
                    <span className='font-medium text-brand-navy'>{entry.fieldChanged}</span>
                  </div>
                )}
                <div className='flex items-stretch gap-2'>
                  <div className='flex-1 rounded-md bg-red-50 border border-red-100 p-2'>
                    <div className='text-xs uppercase tracking-wider text-red-500 mb-1'>Old</div>
                    <div className='text-sm text-brand-navy whitespace-pre-wrap break-words'>
                      {entry.oldValue ?? '—'}
                    </div>
                  </div>
                  <div className='flex items-center'>
                    <ArrowRight className='h-4 w-4 text-brand-gray' />
                  </div>
                  <div className='flex-1 rounded-md bg-emerald/5 border border-emerald/20 p-2'>
                    <div className='text-xs uppercase tracking-wider text-emerald mb-1'>New</div>
                    <div className='text-sm text-brand-navy whitespace-pre-wrap break-words'>
                      {entry.newValue ?? '—'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Note */}
          {entry.noteText && (
            <Card className='border-gray-200'>
              <CardContent className='pt-4'>
                <div className='text-xs uppercase tracking-wider text-brand-gray mb-1'>Note</div>
                <div className='text-sm text-brand-navy whitespace-pre-wrap'>{entry.noteText}</div>
              </CardContent>
            </Card>
          )}

          {/* Raw details JSON */}
          {detailsJson && (
            <Card className='border-gray-200'>
              <CardContent className='pt-4'>
                <div className='text-xs uppercase tracking-wider text-brand-gray mb-1'>Details</div>
                <pre className='text-xs bg-gray-50 rounded-md p-3 overflow-x-auto text-brand-navy'>
                  {detailsJson}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
